import { useEffect, useState } from "react";
import "../styles/RegisterPage.css";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import GoogleAuth from "../objects/GoogleAuth";
import MicrosoftAuth from "../objects/MicrosoftAuth";
import { IndexHeaderNav } from "./IndexHeaderNav";
import * as Sentry from '@sentry/react';

function RegisterEmail({ source = "page"}) {
    const [waiting, setWaiting] = useState(false);
    const [firstWait, setFirstWait] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [email, setEmail] = useState("");

    const [adminReg, setAdminReg] = useState(null);

    const navigate = useNavigate();
    const { theme } = useTheme();
    const [provider, setProvider] = useState("");

    const hostname = window.location.hostname.toLowerCase();

        /*
    // True if on purdue.rezify.ai, purdue.rezify.live, purdue.rezify.local, etc.
    const isPurdueSubdomain =
      hostname.startsWith("purdue.") && hostname.includes("rezify");


    // Helper: true if email ends with @purdue.edu (case-insensitive)
    const isPurdueEmail = (val) => {
      const e = (val || "").trim().toLowerCase();
      return e.endsWith("@purdue.edu") && !isPurdueSubdomain;
    };


    const [showPurdueGate, setShowPurdueGate] = useState(false);
    const [purdueGateDismissed, setPurdueGateDismissed] = useState(false);
        */


    const fromResults = source === "results";

    const providerLower = (provider || "").toLowerCase();

    const showGoogleAuth = providerLower === "google" || providerLower === "any";
    const showMicrosoftAuth = false; // providerLower === "microsoft" || providerLower === "any";



    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant", // or "smooth" if you want
        });
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("oauthError") === "true") {
            setErrorMessage("Google sign-in failed or could not be verified. Please try again.");
        }
    }, []);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setAdminReg(params.get("type") === "admin");
    }, []);

    // Autofill email from resume via existing GET /api/register
    useEffect(() => {
        if (adminReg === null) return;

        const endpoint = adminReg ? "/api/admin_register" : "/api/register";

        (async () => {
            try {
                const response = await fetch(`${endpoint}${window.location.search}`);
                const data = await response.json();

                if (!response.ok) {
                    setErrorMessage(data.error_message || 'Failed to load. Please try again.');
                    return;
                }

                // Autofill email if present (works on page + results popup)
                if (data.resume_info && data.resume_info.email) {
                    setEmail(data.resume_info.email);
                }

                if (data.error_message) {
                    setErrorMessage(data.error_message);
                }
                if (data.colors && typeof data.colors.provider === "string") {
                    setProvider(data.colors.provider);
                } else {
                    setProvider("");
                }
            } catch (e) {
                Sentry.captureException(e);
                setErrorMessage('Network error. Please try again.');
            } finally {
                setFirstWait(true);
            }
        })();
    }, [adminReg]);

    const handleContinue = async (event) => {
      event.preventDefault();

      // Read submitted email from the form (source of truth on submit)
      const formData = new FormData(event.target);
      const submittedEmail = (formData.get("email") || "").toString();

        /*
      // Gate purdue.edu
      if (isPurdueEmail(submittedEmail)) {
        setShowPurdueGate(true);

        if (!purdueGateDismissed) {
          setErrorMessage(
            "Trying to sign up with a class code? Please register at https://purdue.rezify.ai/register"
          );
          setWaiting(false);
          return; // 🚫 do not proceed
        }
      }
         */

      setWaiting(true);
      setErrorMessage("");

      // explicitly set email + reset resume flag on submit
      setEmail(submittedEmail);

      const endpoint = adminReg ? "/api/admin_register_email" : "/api/register_email";

      try {
        const result = await fetch(endpoint, {
          method: "POST",
          body: formData,
        });

        const data = await result.json();

        if (!result.ok) {
          setErrorMessage(data.error_message || 'An error occurred. Please try again.');
          return;
        }

        if (data.redirect) {
          window.location.href = data.redirect;
          return;
        }

        if (data.error_message) {
          setErrorMessage(data.error_message);
        }
      } catch (e) {
        Sentry.captureException(e);
        setErrorMessage('Network error. Please try again.');
      } finally {
        setWaiting(false);
      }
    };


    const headerTitle = adminReg
        ? "Admin Register"
        : fromResults
            ? "Register"
            : "Register";

    const subtitleText = fromResults
        ? "Create an account to access your matches. No payment necessary."
        : "Please enter your email to get started. No payment necessary.";

    const backButtonText = fromResults ? "Login" : "← Back";

    return (
        <div className="register-page">
            {!fromResults && (
                <IndexHeaderNav/>
            )}
            <div className="register-shell">
                <div className="register-card">
                    <div className="register-header">
                        <div className="register-header-left">
                            <button
                                className="back-btn"
                                type="button"
                                onClick={() => {
                                    if (adminReg) {
                                        navigate("/login?type=admin");
                                    } else {
                                        navigate("/login");
                                    }
                                }}
                            >
                                {backButtonText}
                            </button>
                        </div>

                        <div className="register-header-center">
                            <h1>{headerTitle}</h1>
                        </div>

                        <div className="register-header-right">
                            {adminReg && (
                                <img
                                    src={theme.logo}
                                    alt="Rezify Logo"
                                    className="admin-logo"
                                />
                            )}
                        </div>
                    </div>

                    <p className="muted">{subtitleText}</p>

                    {!firstWait && (
                        <div className="center-row">
                            <div className="spinner-black" />
                        </div>
                    )}

                    {firstWait && (
                        <>
                            {/* Email + Continue */}
                            <form
                                method="post"
                                className="register-form"
                                onSubmit={handleContinue}
                            >
                                <div className="field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        aria-label="Email address"
                                        placeholder="you@school.edu"
                                        required
                                        value={email}
                                        onChange={(e) => {
                                          const nextEmail = e.target.value;
                                          setEmail(nextEmail);
                                            /*
                                          if (isPurdueEmail(nextEmail)) {
                                            setShowPurdueGate(true);
                                            setErrorMessage("");              // optional: clear other errors so gate is prominent
                                            setPurdueGateDismissed(false);    // must dismiss each time they enter purdue email
                                          } else {
                                            setShowPurdueGate(false);
                                            setPurdueGateDismissed(false);
                                          }
                                            */
                                        }}

                                    />
                                </div>

                                    {/*
                                {showPurdueGate && !purdueGateDismissed && (
                                  <div className="error-box" style={{ position: "relative" }}>
                                    <button
                                      type="button"
                                      aria-label="Dismiss"
                                      onClick={() => {
                                        setPurdueGateDismissed(true);
                                        // keep the message text around or clear it—your call:
                                        // setErrorMessage("");
                                      }}
                                      style={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        fontSize: 14,
                                        lineHeight: 1,
                                      }}
                                    >
                                      Close
                                    </button>

                                    <div style={{ paddingRight: 24 }}>
                                      Trying to sign up with a class code? Please register at{" "}
                                      <a href="https://purdue.rezify.ai/register">https://purdue.rezify.ai/register</a>
                                    </div>
                                  </div>
                                )}
                                    */}


                                {errorMessage.length >= 1 && (
                                    <div className="error-box">
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    className="primary-btn"
                                    type="submit"
                                    // disabled={waiting || (showPurdueGate && !purdueGateDismissed)} - for purdue gate
                                    disabled={waiting}
                                >
                                    {waiting ? (
                                        <span className="btn-inline">
                                            <span className="spinner" />
                                        </span>
                                    ) : (
                                        "Continue"
                                    )}
                                </button>
                            </form>

                            {/* Provider Auth */}
                            {(showGoogleAuth || showMicrosoftAuth) && (
                                <>
                                    <div className="or-divider">
                                        <span className="or-divider-text">or</span>
                                    </div>

                                    <div
                                        className="auth-grid"
                                        data-count={(showGoogleAuth ? 1 : 0) + (showMicrosoftAuth ? 1 : 0)}
                                    >
                                        {showGoogleAuth && (
                                            <div className="auth-item">
                                                <GoogleAuth
                                                    next={adminReg ? "/register_complete?type=admin" : "/register_complete"}
                                                    cameFrom={adminReg ? "admin_register" : "register"}
                                                />
                                            </div>
                                        )}

                                        {showMicrosoftAuth && (
                                            <div className="auth-item">
                                                <MicrosoftAuth
                                                    next={adminReg ? "/register_complete?type=admin" : "/register_complete"}
                                                    cameFrom={adminReg ? "admin_register" : "register"}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export { RegisterEmail };
